import { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent.toLowerCase());
const isStandalone = () =>
  (window.navigator.standalone === true) ||
  window.matchMedia('(display-mode: standalone)').matches;

export function PWAInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [showIOS, setShowIOS] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;

    const dismissed = localStorage.getItem('pwa-dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 7 * 24 * 60 * 60 * 1000) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    if (isIOS()) {
      const timer = setTimeout(() => setShowIOS(true), 3000);
      return () => { clearTimeout(timer); window.removeEventListener('beforeinstallprompt', handler); };
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setShow(false);
  };

  const handleDismiss = () => {
    setShow(false);
    setShowIOS(false);
    localStorage.setItem('pwa-dismissed', Date.now().toString());
  };

  if (!show && !showIOS) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] md:hidden" data-testid="pwa-banner">
      <div className="bg-white border-t-[3px] border-[#1D9E75] shadow-2xl p-4 safe-area-inset-bottom">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-[#1D9E75] rounded-2xl flex items-center justify-center flex-shrink-0 shadow-md">
            <span className="text-white font-bold text-xl" style={{ fontFamily: "'Work Sans', sans-serif" }}>G</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-[#085041] text-sm">Instalar GuiaCit</p>
            <p className="text-gray-500 text-xs leading-tight mt-0.5">
              {showIOS
                ? 'Toque em Compartilhar e depois "Adicionar à Tela de Início"'
                : 'Adicione à tela inicial para acesso rápido'}
            </p>
          </div>
          {show && !showIOS && (
            <button
              onClick={handleInstall}
              className="bg-[#1D9E75] text-white text-sm font-semibold px-4 py-2 rounded-xl flex-shrink-0 active:bg-[#0F6E56] transition-colors"
              data-testid="pwa-install-btn"
            >
              Instalar
            </button>
          )}
          <button
            onClick={handleDismiss}
            className="p-1.5 rounded-lg hover:bg-gray-100 flex-shrink-0 transition-colors"
            data-testid="pwa-dismiss-btn"
            aria-label="Fechar"
          >
            <X size={18} className="text-gray-400" />
          </button>
        </div>

        {showIOS && (
          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-center gap-2 text-xs text-gray-500">
            <span>1. Toque em</span>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#007AFF" strokeWidth="2.5">
              <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>2. "Adicionar à Tela de Início"</span>
          </div>
        )}
      </div>
    </div>
  );
}
